import clsx from "clsx";
import { msg, t } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { Plural, Trans } from "@lingui/react/macro";
import {
  Cell,
  Column,
  Row,
  Table,
  TableBody,
  TableHeader,
} from "react-aria-components";
import {
  medicalCompetenceDictionary,
  orderedCompetences,
  type IMedicalCompetence,
} from "@medoma/models/shifts";
import {
  partOfDaySchema,
  type DayBreakdownData,
  type PartOfDay,
} from "@/api/Capacity";
import type { IActivityCategory } from "@medoma/models/activities";
import styles from "./DayBreakdownPanel.module.scss";
import { Text } from "@medoma/components/Text/Text";
import { CapacityMeter } from "../CapacityMeter";
import { CategoryIcon } from "@/components/CategoryIcon/CategoryIcon";
import { CompetenceChip } from "@/components/Chips/CompetenceChip";
import {
  getCompetenceGroupBreakdownData,
  getVisitCounts,
  aggregateVisitCounts,
  getAggregatedCellData,
  getCellData,
  type CellData,
  groupedCompetences,
  aggregateAdminCounts,
  calculateAvailableHours,
} from "./dayBreakdownUtils";
import { sumCounts } from "@/Utils/numberUtils";
import { VisitsPerShiftChip } from "../VisitsPerShiftChip";
import NoResults from "@/components/NoResults/NoResults";
import { calculateIncludedShiftsCount } from "../capacityOverviewUtils";

const partOfDayDictionary: Record<PartOfDay, MessageDescriptor> = {
  day: msg`Dag`,
  evening: msg`Kväll`,
  night: msg`Natt`,
};

type BreakdownRow = {
  id: string;
  header: React.ReactNode;
  cells: { partOfDay: PartOfDay; cellData: CellData | null }[];
};

const competenceHasShifts = (dayBreakdownData: DayBreakdownData) =>
  partOfDaySchema.options.some(
    (partOfDay) =>
      calculateIncludedShiftsCount(
        dayBreakdownData[partOfDay]?.shiftsByType ?? [],
      ) > 0,
  );

const competenceHasVisibleData = (dayBreakdownData: DayBreakdownData) =>
  partOfDaySchema.options.some(
    (partOfDay) =>
      getCellData({ competenceBreakdownData: dayBreakdownData, partOfDay }) !==
      null,
  );

const RowHeaderLabel = ({ competence }: { competence: IMedicalCompetence }) => (
  <Text element="span" size="small" weight="bold">
    {t(medicalCompetenceDictionary[competence].long)}
  </Text>
);

const ActivityCategoryCount = ({
  count,
  category,
}: {
  count: number;
  category: IActivityCategory;
}) => (
  <span className={styles.activityTypeAndCount}>
    <CategoryIcon category={category} size="tiny" />
    <Text element="span" size="small" weight="medium">
      {count}
    </Text>
  </span>
);

const CompetenceTag = ({
  partOfDay,
  dayBreakdownData,
}: {
  partOfDay: PartOfDay;
  dayBreakdownData: DayBreakdownData;
}) => {
  const partOfDayCapacity = dayBreakdownData[partOfDay];
  const { competence, anyTimeOfDayCategoryCounts } = dayBreakdownData;

  // The any time of day visits is only shown in the day column
  const anyTimeOfDayVisitCount =
    partOfDay === partOfDaySchema.enum.day
      ? sumCounts(getVisitCounts(anyTimeOfDayCategoryCounts))
      : 0;
  const partOfDayVisitCount = partOfDayCapacity
    ? sumCounts(getVisitCounts(partOfDayCapacity.categoryCounts))
    : 0;
  const visits = partOfDayVisitCount + anyTimeOfDayVisitCount;
  const shifts = partOfDayCapacity
    ? calculateIncludedShiftsCount(partOfDayCapacity.shiftsByType)
    : 0;

  if (visits === 0 && shifts === 0) {
    return null;
  }

  return (
    <li className={styles.competenceTag}>
      <CompetenceChip competence={competence} state="neutral" size="tiny" />
      <div className={styles.competenceTagStats}>
        <Text element="span" size="small">
          <Plural value={visits} one="# besök" other="# besök" />
        </Text>
        <Text element="span" size="small">
          <Plural value={shifts} one="# pass" other="# pass" />
        </Text>
      </div>
    </li>
  );
};

const EmptyCellContent = () => (
  <Text element="span" size="base" color="no-results">
    <Trans>Inga pass och inga aktiviteter</Trans>
  </Text>
);

const CellContent = ({ cellData }: { cellData: CellData }) => {
  const {
    partOfDayCellCapacity,
    partOfDay,
    anyTimeOfDayVisits,
    anyTimeOfDayAdmins,
    anyTimeOfDayBookedHours,
  } = cellData;
  const {
    includedShiftsCount,
    bookedHours,
    totalStaffedHours,
    visitCounts,
    adminActivityCounts,
  } = partOfDayCellCapacity;

  // Include the any time of day activities in respective count since we want to include them in the day column
  const allCellAdminCounts = aggregateAdminCounts([
    ...adminActivityCounts,
    ...anyTimeOfDayAdmins,
  ]);
  const allCellVisitCounts = aggregateVisitCounts([
    ...visitCounts,
    ...anyTimeOfDayVisits,
  ]);

  const allCellBookedHours = anyTimeOfDayBookedHours + bookedHours;

  const totalAdminCount = sumCounts(allCellAdminCounts);
  const totalVisitCount = sumCounts(allCellVisitCounts);

  const anyTimeOfDayVisitCount = sumCounts(anyTimeOfDayVisits);
  const visitsPerShift =
    includedShiftsCount > 0 ? totalVisitCount / includedShiftsCount : null;

  const availableHours = calculateAvailableHours({
    includedShiftsCount,
    totalStaffedHours,
    bookedHours: allCellBookedHours,
    visitCounts: allCellVisitCounts,
  });

  // Only show the competence tags if it is a competence group with at least two competences
  const shouldShowCompetenceTags =
    cellData.kind === "group" &&
    cellData.competenceGroupBreakdownData.length > 1;

  return (
    <div className={styles.cellContent}>
      <VisitsPerShiftChip visitsPerShift={visitsPerShift} />
      <div className={styles.activities}>
        <div className={styles.visitCounts}>
          <Text
            element="span"
            size="large"
            weight="bold"
            className={styles.totalCount}
          >
            <Plural value={totalVisitCount} one="# besök" other="# besök" />
          </Text>
          <span className={styles.activityTypes}>
            {allCellVisitCounts.map(({ category, count }) => (
              <ActivityCategoryCount
                key={category}
                category={category}
                count={count}
              />
            ))}
          </span>
        </div>
        {anyTimeOfDayVisitCount > 0 ? (
          <Text
            element="span"
            size="smallest"
            color="faded"
            className={styles.anyTimeOfDayVisits}
          >
            <Trans>
              Varav {anyTimeOfDayVisitCount} besök när som helst under dagen
            </Trans>
          </Text>
        ) : null}
        <div
          className={clsx(
            styles.adminActivityCounts,
            anyTimeOfDayVisitCount > 0 && styles.adminCountsAfterAnyTime,
          )}
        >
          <Text element="span" weight="medium" className={styles.totalCount}>
            <Plural value={totalAdminCount} one="# övrig" other="# övriga" />
          </Text>
          <span className={styles.activityTypes}>
            {allCellAdminCounts.map(({ category, count }) => (
              <ActivityCategoryCount
                key={category}
                category={category}
                count={count}
              />
            ))}
          </span>
        </div>
      </div>
      <div className={styles.staffing}>
        <Text element="span">
          {t`Arbetspass`}: {includedShiftsCount}
        </Text>
        <CapacityMeter
          availableHours={availableHours}
          totalHours={totalStaffedHours}
        />
      </div>

      {shouldShowCompetenceTags ? (
        <ul className={styles.competenceTags}>
          {cellData.competenceGroupBreakdownData.map((dayBreakdownData) => (
            <CompetenceTag
              key={dayBreakdownData.competence}
              dayBreakdownData={dayBreakdownData}
              partOfDay={partOfDay}
            />
          ))}
        </ul>
      ) : null}
    </div>
  );
};

export const DayBreakdownPanel = ({
  dayBreakdownData,
}: {
  dayBreakdownData: DayBreakdownData[];
}) => {
  // Competences whose work frequently overlaps in practice are grouped into one row
  const groupedCompetenceSet = new Set(groupedCompetences.flat());

  // The rest of the competences are displayed in separate rows, sorted by competence
  const ungroupedBreakdownData = dayBreakdownData
    .filter(({ competence }) => !groupedCompetenceSet.has(competence))
    .sort(
      (a, b) =>
        orderedCompetences.indexOf(a.competence) -
        orderedCompetences.indexOf(b.competence),
    );

  const groupedRows: BreakdownRow[] = groupedCompetences
    .map((competences) =>
      getCompetenceGroupBreakdownData({ dayBreakdownData, competences }),
    )
    .filter((group) => group.length > 0)
    .map((group) => {
      const competencesWithShifts = group.filter(competenceHasShifts);
      const competencesInheading =
        competencesWithShifts.length > 0
          ? competencesWithShifts
          : group.filter(competenceHasVisibleData);

      return {
        id: group.map(({ competence }) => competence).join(),
        header: competencesInheading.map(({ competence }) => (
          <div key={competence} className={styles.competenceInGroupHeading}>
            <RowHeaderLabel competence={competence} />
          </div>
        )),
        cells: partOfDaySchema.options.map((partOfDay) => ({
          partOfDay,
          cellData: getAggregatedCellData({
            competenceGroupBreakdownData: group,
            partOfDay,
          }),
        })),
      };
    });

  const ungroupedRows: BreakdownRow[] = ungroupedBreakdownData.map(
    (competenceBreakdownData) => ({
      id: competenceBreakdownData.competence,
      header: (
        <RowHeaderLabel competence={competenceBreakdownData.competence} />
      ),
      cells: partOfDaySchema.options.map((partOfDay) => ({
        partOfDay,
        cellData: getCellData({ competenceBreakdownData, partOfDay }),
      })),
    }),
  );

  // Hide competence rows whose cells are all empty
  const rows = [...groupedRows, ...ungroupedRows].filter(({ cells }) =>
    cells.some(({ cellData }) => cellData !== null),
  );

  if (rows.length === 0) {
    return (
      <NoResults
        message={t`Ingen mer information att visa för denna dag`}
        padding={32}
      />
    );
  }

  return (
    <div className={styles.dayBreakdownPanel}>
      <Table aria-label={t`Kapacitet per kompetens`} className={styles.table}>
        <TableHeader>
          <Column isRowHeader aria-label={t`Kompetenser`} />
          {partOfDaySchema.options.map((partOfDay) => (
            <Column key={partOfDay} className={styles.columnHeader}>
              <Text
                className={styles.columnHeaderLabel}
                element="span"
                size="base"
                weight="medium"
              >
                {t(partOfDayDictionary[partOfDay])}
              </Text>
            </Column>
          ))}
        </TableHeader>
        <TableBody>
          {rows.map(({ id, header, cells }) => (
            <Row key={id} id={id}>
              <Cell className={styles.rowHeader}>
                <span className={styles.rowHeaderLabels}>{header}</span>
              </Cell>
              {cells.map(({ partOfDay, cellData }) => (
                <Cell
                  key={partOfDay}
                  className={cellData ? styles.cell : styles.emptyCell}
                >
                  {cellData ? (
                    <CellContent cellData={cellData} />
                  ) : (
                    <EmptyCellContent />
                  )}
                </Cell>
              ))}
            </Row>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
